import { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import DropZone from "../components/DropZone";
import { invoices } from "../services/api";
import { computeSha256 } from "../utils/hash";

// ─── Types ──────────────────────────────────────────────────────────────────

type UploadStep =
  | "idle"
  | "hashing"
  | "requesting-url"
  | "uploading"
  | "creating"
  | "done"
  | "error";

interface FileUploadState {
  file: File;
  step: UploadStep;
  progress: number; // 0-100 for the S3 PUT
  invoiceId: string | null;
  error: string | null;
  hash: string | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function contentTypeFor(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return "application/pdf";
  if (ext === "csv") return "text/csv";
  if (ext === "xlsx")
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  return "application/octet-stream";
}

/** Upload a single file to S3 via pre-signed URL with progress tracking. */
function uploadToS3(
  url: string,
  file: File,
  contentType: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", contentType);

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener("error", () => reject(new Error("Network error during upload")));
    xhr.addEventListener("abort", () => reject(new Error("Upload aborted")));

    xhr.send(file);
  });
}

// ─── Step label ─────────────────────────────────────────────────────────────

function stepLabel(step: UploadStep): string {
  switch (step) {
    case "idle":
      return "Waiting";
    case "hashing":
      return "Computing hash...";
    case "requesting-url":
      return "Requesting upload URL...";
    case "uploading":
      return "Uploading to storage...";
    case "creating":
      return "Creating invoice...";
    case "done":
      return "Complete";
    case "error":
      return "Failed";
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function UploadPage() {
  const navigate = useNavigate();
  const [uploads, setUploads] = useState<FileUploadState[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const updateUpload = useCallback(
    (index: number, patch: Partial<FileUploadState>) => {
      setUploads((prev) =>
        prev.map((u, i) => (i === index ? { ...u, ...patch } : u)),
      );
    },
    [],
  );

  const processFile = useCallback(
    async (file: File, index: number) => {
      try {
        // Step 1: Hash
        updateUpload(index, { step: "hashing" });
        const hash = await computeSha256(file);
        updateUpload(index, { hash, step: "requesting-url" });

        // Step 2: Get pre-signed URL
        const ct = contentTypeFor(file);
        const { upload_url, file_key } = await invoices.getUploadUrl(
          file.name,
          ct,
        );
        updateUpload(index, { step: "uploading" });

        // Step 3: Upload to S3 with progress
        await uploadToS3(upload_url, file, ct, (pct) => {
          updateUpload(index, { progress: pct });
        });
        updateUpload(index, { step: "creating" });

        // Step 4: Create invoice record
        // The backend infers the invoice_type from file content.
        // We send file_key; the hash is sent for dedup checks.
        const { invoice } = await invoices.create({
          file_key,
          file_hash: hash,
          original_filename: file.name,
          content_type: ct,
        } as Parameters<typeof invoices.create>[0] & {
          file_hash: string;
          original_filename: string;
          content_type: string;
        });

        updateUpload(index, {
          step: "done",
          progress: 100,
          invoiceId: invoice.id,
        });

        return invoice.id;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Unknown error occurred";
        updateUpload(index, { step: "error", error: message });
        return null;
      }
    },
    [updateUpload],
  );

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (isUploading) return;
      setIsUploading(true);

      // Initialize upload states
      const initial: FileUploadState[] = files.map((file) => ({
        file,
        step: "idle",
        progress: 0,
        invoiceId: null,
        error: null,
        hash: null,
      }));
      setUploads(initial);

      // Process files sequentially to avoid overwhelming the backend
      const results: (string | null)[] = [];
      for (let i = 0; i < files.length; i++) {
        const id = await processFile(files[i], i);
        results.push(id);
      }

      setIsUploading(false);

      // If single file and successful, redirect to detail page
      if (files.length === 1 && results[0]) {
        navigate(`/invoice/${results[0]}`);
      }
    },
    [isUploading, navigate, processFile],
  );

  const handleReset = useCallback(() => {
    setUploads([]);
  }, []);

  const isBulk = uploads.length > 1;
  const allDone = uploads.length > 0 && uploads.every((u) => u.step === "done" || u.step === "error");

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900">Upload Invoice</h1>
      <p className="mt-1 text-sm text-gray-600">
        Upload haulier PDFs or warehouse CSVs for verification.
      </p>

      {/* Drop zone (hidden during active upload in single-file mode) */}
      {(uploads.length === 0 || (allDone && isBulk)) && (
        <div className="mt-6">
          <DropZone onFiles={handleFiles} disabled={isUploading} />
          <p className="mt-3 text-xs text-gray-500">
            Palletways: upload the PDF. Box Logistics: upload BOTH the CSV and
            the PDF (they pair by invoice number). Warehouse: upload the
            CSV/XLSX report.
          </p>
        </div>
      )}

      {/* Upload progress list */}
      {uploads.length > 0 && (
        <div className="mt-6 space-y-3">
          {uploads.map((u, i) => (
            <div
              key={i}
              className="rounded-lg border border-gray-200 bg-white p-4"
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {u.file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatSize(u.file.size)}
                  </p>
                </div>
                <div className="ml-4 flex items-center gap-3">
                  <span
                    className={`text-xs font-medium ${
                      u.step === "done"
                        ? "text-green-600"
                        : u.step === "error"
                          ? "text-red-600"
                          : "text-blue-600"
                    }`}
                  >
                    {stepLabel(u.step)}
                  </span>
                  {u.step === "done" && u.invoiceId && (
                    <Link
                      to={`/invoice/${u.invoiceId}`}
                      className="rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                    >
                      View
                    </Link>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              {(u.step === "uploading" ||
                u.step === "hashing" ||
                u.step === "requesting-url" ||
                u.step === "creating") && (
                <div className="mt-2">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full rounded-full bg-blue-600 transition-all duration-300"
                      style={{
                        width:
                          u.step === "uploading" ? `${u.progress}%` : "100%",
                      }}
                    />
                  </div>
                  {u.step === "uploading" && (
                    <p className="mt-1 text-right text-xs text-gray-500">
                      {u.progress}%
                    </p>
                  )}
                </div>
              )}

              {/* Indeterminate bar for non-upload steps */}
              {(u.step === "hashing" ||
                u.step === "requesting-url" ||
                u.step === "creating") && (
                <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-200">
                  <div className="h-full w-1/3 animate-pulse rounded-full bg-blue-400" />
                </div>
              )}

              {/* Error message */}
              {u.step === "error" && u.error && (
                <p className="mt-2 text-xs text-red-600">{u.error}</p>
              )}
            </div>
          ))}

          {/* Reset / Upload more button */}
          {allDone && isBulk && (
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={handleReset}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
              >
                Upload more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
